import React, { useCallback, useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { XmppContext } from "../stream/xmppClient"
import css from "./ChatListHeader.module.css"
import JoinChatPopover from "./JoinChatPopover"
import SearchPopover from "./search/SearchPopover"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useAgentForDriverPerChat } from "@/hooks/useAgentForDriverPerChat"
import { useUserPermissions } from "@/hooks/useUserPermissions"
import { cn } from "@/lib/utils"
import { useAppDispatch, useAppSelector } from "../reducers/hooks"
import {
  hideArchive,
  hideGlobalSearch,
  isArchiveVisible,
  isGlobalSearchVisible,
  selectChatListMode,
  selectLastActiveChatJid,
  setChatListMode,
  showArchive,
  showGlobalSearch,
} from "../reducers/overlaysSlice"
import { fetchDispatcherChats } from "../reducers/dispatcherChatsThunks"
import { isMobile } from "../utils/detectDevice"
import { mobileRoutes, setMobileRoute } from "../routes/routes"
import { markAllChatsAsRead } from "../reducers/chatsSliceThunks"
import { getWhatsappStatus } from "../reducers/rosterThunks"
import { captureError } from "../ErrorHandlers"
import WhatsappAccountStatusDialog from "./WhatsappAccountStatusDialog"
import Cookies from "universal-cookie"
import { isAndroid } from "react-device-detect"
import { isVersionAtLeast } from "../utils/mobileVersion"
import { EditCrewModal } from "./driverManagement/EditCrewModal"
import AddPlatformScienceCarModal from "./driverManagement/AddPlatformScienceCarModal"
import AddTransicsCarModal from "./driverManagement/AddTransicsCarModal"
import { useDarkMode } from "../contexts/DarkModeContext"
import { deleteIndexDB } from "../reducers/store"
import AdHocCommands from "../api/adHocCommands"
import { AddEditUserModal } from "./userManagement/AddEditUserModal"
import { selectArchivedChats } from "@/reducers/chatsSettingsSelectors"
import { changeActiveChat } from "@/reducers/chatsSlice"
import { selectActiveChat } from "@/reducers/chatsSliceSelectors"
import { Button } from "./ui/button"
import {
  Archive,
  ArrowLeft,
  Car,
  CheckCheck,
  EllipsisVertical,
  LogOut,
  MessageCirclePlus,
  Moon,
  Satellite,
  SearchIcon,
  Sun,
  User,
  UserPlus,
  Users,
} from "lucide-react"
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from "./ui/menubar"
import RoomLoading from "@/components/RoomLoading"
import { SlickAIButton } from "@/components/SlickAIButton"
import type { TabType } from "@/reducers/tabsSlice"

type ChatListHeaderProps = {
  onOpenChatInfo: () => void
  onActiveChatChange: (chatJid: string, tab?: TabType) => void
}

const ChatListHeader = ({
  onOpenChatInfo,
  onActiveChatChange,
}: ChatListHeaderProps) => {
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)
  const dispatch = useAppDispatch()

  const [whatsappStatus, setWhatsappStatus] = useState<string | undefined>(
    undefined,
  )

  const [isAddNewUserModalOpen, setIsAddNewUserModalOpen] = useState(false)

  const [accountStatusDialogOpen, setAccountStatusDialogOpen] =
    useState<boolean>(false)

  const archiveVisible = useAppSelector(isArchiveVisible)
  const archivedChats = useAppSelector(selectArchivedChats)
  const showArchiveButton = archivedChats.length > 0 && !archiveVisible

  useEffect(() => {
    dispatch(getWhatsappStatus(client))
      .then(setWhatsappStatus)
      .catch((e) => {
        captureError(e, { origin: "Other" })
      })
  }, [client, dispatch])

  const activeChat = useAppSelector(selectActiveChat)

  const searchVisible = useAppSelector(isGlobalSearchVisible)

  const handleShowSearch = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      // @ts-ignore
      e.currentTarget?.blur?.()
      dispatch(showGlobalSearch())
      if (isMobile) {
        setMobileRoute(mobileRoutes.SEARCH)
      }
    },
    [dispatch],
  )

  const handleShowArchived = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      // @ts-ignore
      e.currentTarget?.blur?.()
      if (isMobile) {
        setMobileRoute(mobileRoutes.ARCHIVE)
      }
      dispatch(showArchive({ lastActiveChatJid: activeChat.jid }))
      dispatch(changeActiveChat({ activeChatJid: archivedChats[0] }))
    },
    [activeChat, archivedChats, dispatch],
  )

  const handleCloseSearch = useCallback(() => {
    if (isMobile) {
      setMobileRoute(mobileRoutes.CHAT_LIST)
    }
    dispatch(hideGlobalSearch())
  }, [dispatch])

  const onMarkAllChatsReadClick = useCallback(() => {
    dispatch(markAllChatsAsRead(client))
  }, [client, dispatch])

  const onSetWhatsAppAccountStatus = () => {
    setAccountStatusDialogOpen(true)
  }

  const closeAccountStatusDialog = useCallback(() => {
    setAccountStatusDialogOpen(false)
  }, [])

  const onSaveAccountStatus = useCallback(
    async (status: string) => {
      closeAccountStatusDialog()
      setWhatsappStatus(status)
    },
    [closeAccountStatusDialog],
  )

  const onSignOut = useCallback(async () => {
    await AdHocCommands.disablePushNotificationsSubscription(client)
    await client.stop()
    const cookies = new Cookies(null, { path: "/" })
    // Remove cookies with all possible attribute combinations to ensure proper deletion
    cookies.remove("email", { path: "/" })
    cookies.remove("email", { path: "/", domain: window.location.hostname })
    cookies.remove("email") // default case

    cookies.remove("password", { path: "/" })
    cookies.remove("password", { path: "/", domain: window.location.hostname })
    cookies.remove("password") // default case

    window.persistor.purge()
    if (!window.ReactNativeWebView) {
      try {
        await deleteIndexDB()
      } catch (e) {
        console.error(e)
      }
    }

    if (window.ReactNativeWebView) {
      const message = JSON.stringify({ type: "SignOut", data: null })
      window.ReactNativeWebView.postMessage(message)
    } else {
      window.location.reload()
    }
  }, [client])

  const showSignOut =
    !isAndroid ||
    (isAndroid &&
      (isVersionAtLeast(window.ANDROID_APP_VERSION, "2.2.0") ||
        !window.ReactNativeWebView))

  const isTransicsCarEnabled = Boolean(useFeatureFlagEnabled("transics-car"))
  const isPlatformScienceCarEnabled = Boolean(
    useFeatureFlagEnabled("platform-science-car"),
  )

  // A fleet manager reads the whole fleet grouped by dispatcher here, no flag
  // needed; the flag keeps the read-only view for the accounts that had it.
  const { membershipLocked, isFleetManager } = useUserPermissions()
  const dispatcherViewEnabled =
    Boolean(useFeatureFlagEnabled("dispatcher-chats-view")) || isFleetManager
  const chatListMode = useAppSelector(selectChatListMode)

  const handleToggleChatListMode = useCallback(() => {
    const next = chatListMode === "dispatchers" ? "chats" : "dispatchers"
    dispatch(setChatListMode(next))
    if (next === "dispatchers") {
      dispatch(fetchDispatcherChats(client))
    }
  }, [chatListMode, dispatch, client])

  const agentPerChatEnabled = useAgentForDriverPerChat()

  const [isAddCarModalOpen, setIsAddCarModalOpen] = useState(false)
  const [isAddTransicsCarModalOpen, setIsAddTransicsCarModalOpen] =
    useState(false)
  const [
    isAddPlatformScienceCarModalOpen,
    setIsAddPlatformScienceCarModalOpen,
  ] = useState(false)
  const { isDarkMode, toggleDarkMode } = useDarkMode()

  const lastActiveChatJid = useAppSelector(selectLastActiveChatJid)

  const handleCloseArchive = useCallback(() => {
    if (isMobile) {
      setMobileRoute(mobileRoutes.CHAT_LIST)
    }
    dispatch(hideArchive())
    if (lastActiveChatJid) {
      dispatch(changeActiveChat({ activeChatJid: lastActiveChatJid }))
    }
  }, [dispatch, lastActiveChatJid])

  if (archiveVisible) {
    return (
      <div className={css.chatListHeader}>
        <div className="flex flex-1 flex-row items-center justify-between">
          <Button
            variant="ghost"
            onClick={handleCloseArchive}
            className="self-start pl-0 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("mainMenu.back")}
          </Button>
          <div className="flex flex-row items-center justify-items-end gap-1 text-[#5f6b7c] dark:text-[#abb3bf]">
            <Archive className="h-4 w-4" />
            <div className="text-md font-medium">{t("mainMenu.archive")}</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <WhatsappAccountStatusDialog
        isOpen={accountStatusDialogOpen}
        status={whatsappStatus}
        onSaveSuccess={onSaveAccountStatus}
        onClose={closeAccountStatusDialog}
      />
      <EditCrewModal
        isOpen={isAddCarModalOpen}
        onClose={() => setIsAddCarModalOpen(false)}
      />
      <AddTransicsCarModal
        isOpen={isAddTransicsCarModalOpen}
        onClose={() => setIsAddTransicsCarModalOpen(false)}
      />
      <AddPlatformScienceCarModal
        isOpen={isAddPlatformScienceCarModalOpen}
        onClose={() => setIsAddPlatformScienceCarModalOpen(false)}
      />
      <AddEditUserModal
        isOpen={isAddNewUserModalOpen}
        onClose={() => setIsAddNewUserModalOpen(false)}
      />

      <div
        className={cn(
          "relative flex items-center justify-between bg-sidebar",
          agentPerChatEnabled ? "h-16 max-h-16 px-2" : "max-h-[64px] p-2",
        )}
      >
        <RoomLoading />
        <div className="align-center flex flex-row space-x-2 p-2">
          <h1 className="text-2xl font-bold text-foreground">
            {t("mainMenu.chats")}
          </h1>
        </div>
        <div
          className={cn(
            "flex flex-row items-center justify-items-end",
            !agentPerChatEnabled && "px-2",
          )}
        >
          {dispatcherViewEnabled && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={
                chatListMode === "dispatchers"
                  ? t("mainMenu.showMyChats")
                  : t("mainMenu.showDispatcherChats")
              }
              onClick={handleToggleChatListMode}
            >
              <Users
                className={
                  chatListMode === "dispatchers"
                    ? "h-5 w-5 text-base-primary"
                    : "h-5 w-5"
                }
              />
            </Button>
          )}
          {!isMobile && (
            <SlickAIButton onActiveChatChange={onActiveChatChange} />
          )}
          <>
            <Button
              className="dark:hover:bg-gray-800 dark:focus:bg-gray-800 dark:active:bg-gray-800"
              variant="ghost"
              size="icon"
              data-testid="global-search-button"
              onClick={handleShowSearch}
            >
              <SearchIcon />
            </Button>
            <SearchPopover isOpen={searchVisible} onClose={handleCloseSearch} />
          </>
          {!membershipLocked && (
            <JoinChatPopover onActiveChatChange={onActiveChatChange} />
          )}
          <Menubar>
            <MenubarMenu>
              <MenubarTrigger asChild>
                <Button
                  className="dark:hover:bg-gray-800 dark:focus:bg-gray-800 dark:active:bg-gray-800 data-[state=open]:dark:bg-gray-800"
                  data-testid="action-menu"
                  size="icon"
                  variant="ghost"
                >
                  <EllipsisVertical />
                </Button>
              </MenubarTrigger>
              <MenubarContent className="w-72" align="center">
                <MenubarItem
                  onClick={onOpenChatInfo}
                  disabled={client.status !== "online"}
                >
                  <User className="mr-2 h-4 w-4" />
                  <span>{t("mainMenu.yourAccount")}</span>
                </MenubarItem>
                <MenubarItem
                  onClick={onSetWhatsAppAccountStatus}
                  disabled={client.status !== "online"}
                >
                  <MessageCirclePlus className="mr-2 h-4 w-4" />
                  <span>{t("mainMenu.setAccountStatus")}</span>
                </MenubarItem>
                <MenubarItem onClick={toggleDarkMode}>
                  {isDarkMode ? (
                    <Sun className="mr-2 h-4 w-4" />
                  ) : (
                    <Moon className="mr-2 h-4 w-4" />
                  )}
                  <span>
                    {isDarkMode
                      ? t("mainMenu.switchToLight")
                      : t("mainMenu.switchToDark")}
                  </span>
                </MenubarItem>
                <MenubarSeparator />
                <MenubarItem onClick={() => setIsAddCarModalOpen(true)}>
                  <Car className="mr-2 h-4 w-4" />
                  <span>{t("mainMenu.addCar")}</span>
                </MenubarItem>
                {isTransicsCarEnabled && (
                  <MenubarItem
                    onClick={() => setIsAddTransicsCarModalOpen(true)}
                  >
                    <Satellite className="mr-2 h-4 w-4" />
                    <span>{t("mainMenu.addTransicsCar")}</span>
                  </MenubarItem>
                )}
                {isPlatformScienceCarEnabled && (
                  <MenubarItem
                    onClick={() => setIsAddPlatformScienceCarModalOpen(true)}
                  >
                    <Satellite className="mr-2 h-4 w-4" />
                    <span>{t("mainMenu.addPlatformScienceCar")}</span>
                  </MenubarItem>
                )}
                <MenubarItem onClick={() => setIsAddNewUserModalOpen(true)}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  <span>{t("mainMenu.addWhatsappContact")}</span>
                </MenubarItem>

                <MenubarItem
                  onClick={onMarkAllChatsReadClick}
                  disabled={client.status !== "online"}
                >
                  <CheckCheck className="mr-2 h-4 w-4" />
                  <span>{t("mainMenu.markAllRead")}</span>
                </MenubarItem>
                {showArchiveButton && (
                  <MenubarItem onClick={handleShowArchived}>
                    <Archive className="mr-2 h-4 w-4" />
                    <span>{t("mainMenu.archive")}</span>
                  </MenubarItem>
                )}
                {showSignOut && (
                  <>
                    <MenubarSeparator />
                    <MenubarItem onClick={onSignOut} data-testid="logout">
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>{t("mainMenu.signOut")}</span>
                    </MenubarItem>
                  </>
                )}
              </MenubarContent>
            </MenubarMenu>
          </Menubar>
        </div>
      </div>
    </>
  )
}

export default ChatListHeader
